import Upgrade from '../Upgrade';
import XLSX from 'xlsx';
import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import CodeIcon from '@material-ui/icons/Code';
import PrintIcon from '@material-ui/icons/Print';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { v4 as uuidv4 } from 'uuid';
import EntitlementWarning from '../../core/EntitlementWarning';
import CopyToCipboard from '../../core/icons/CopyToClipboard';
import MenuSelector from '../../core/MenuSelector';
import SecondaryButton from '../../core/SecondaryButton';
import { ENTITLEMENTS as APPENTITLEMENTS } from '../../core/utils/constants';
import GS from '../../grafsync/grafsync';
import Errored from '../Errored';
import { globalStyles } from '../style/gstheme';
import { NS_GS } from '../utils/constants';
import { getGraphFromProject, makeCancelable, sortObjects } from '../utils/utils';
import WebWorker from '../utils/workerSetup';
import Processing from './Processing';
import ReportDownload from './ReportDownload';
import ReportResultsViewer from './ReportResultsViewer';
import ReportsDisplay from './ReportsDisplay.js';
import workerCode from './workers/report-export';
import ReportParameters from './ReportParameters';
import ActionText from '../ActionText';

const styles = () => {
  return Object.assign({
    reportSettings: {
      textAlign: 'right',
      marginBottom: 6,
      padding: '6px 12px',
      borderRadius: 4,
      '@media print': {
        display: 'none'
      }
    },
    reportResults: {
      marginTop: 12
    },
    reports: {
      marginRight: 30,
      marginBottom: 30,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      overflow: 'auto',
      padding: 12
    },
    sparql: {
      overflowX: 'auto',
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word'
    }
  }, globalStyles);
};

class ReportManager extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      usable: GS.getActiveSubscriptionType() !== "Individual" && GS.getActiveSubscriptionType() !== "Standard",
      selectQueryIndex: null,
      savedQueries: [],
      sparql: false,
      processing: "",
      playQuery: undefined,
      reportDescription: "",
      showDatatypes: false,
      processingCancelled: false,
      queries: [],
      cancelledReports: []
    };
    this.cancellable = null;
    this.cancellable2 = null;
  }

  componentDidMount = () => {
    this.getSavedQueries(this.props.graph);
  };

  getSavedQueries = async (graph) => {
    try {
      const data = await this.doSearch({
        search: [{
          predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
          value: NS_GS + "SavedQuery"
        },
        {
          predicate: "https://grafsync.graphifi.com/schema/gs/isReport",
          value: "true",
        }],
        graph: [graph],
        page: 1,
        noCount: true,
        pageSize: 200,
        searchMode: 'Compliant'
      }).promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      const savedQueries = data.results || null;
      sortObjects(savedQueries, "title", this.props.data.defaultLang, null, false);
      // If queries used additional projects we need to work out if the user will be able to run the query
      const queryProjects = savedQueries.map(sq => sq.queryProject || []).flat();
      const queryProjectSet = [...(new Set(queryProjects))];
      await this.doGetProjects(queryProjectSet).promise;
      const access = queryProjectSet.filter(qp => {
        const pg = getGraphFromProject(qp);
        const isLoaded = GS.isLoaded(qp, pg);
        if (!isLoaded) return false;
        const projectData = GS.data(qp, pg);
        // We need this in case deletion happens whilst in memory
        if (projectData.pendingDeletion) return false;
        return true;
      });
      this.setState({ savedQueries, isLoading: false, access });
    }
    catch (e) {
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ isErrored: e, errorMessage: "Could not get saved queries.", isLoading: false });
    }
  };

  doGetProjects(projects) {
    const dataToLoad = projects.map(p => ({
      iri: p,
      graph: getGraphFromProject(p)
    }));
    this.cancellable = makeCancelable(GS.loadDataFromGraph(dataToLoad, {
      predicates: [
        "http://purl.org/dc/terms/title"
      ]
    }, false));
    return this.cancellable;
  }

  doSearch(payload) {
    this.cancellable = makeCancelable(GS.search(payload));
    return this.cancellable;
  }

  doRunSparql(graph, query, queryProjectGraphs = []) {
    this.cancellable2 = makeCancelable(GS.sparqlProject(graph, query, queryProjectGraphs));
    return this.cancellable2;
  }

  doRunSavedSparql(graph, query, params) {
    this.cancellable2 = makeCancelable(GS.runSavedQuery(graph, query, params));
    return this.cancellable2;
  }

  componentWillUnmount() {
    clearTimeout(this.loadingTimeout);
    if (this.cancellable) this.cancellable.cancel();
    if (this.cancellable2) this.cancellable2.cancel();
  }

  selectQuery = async (queryIndex) => {
    const queryInfo = this.state.savedQueries[queryIndex - 1];
    if (queryInfo.queryParameter) {
      this.setState({
        playQuery: queryIndex - 1,
        reportTitle: queryInfo.title[Object.keys(queryInfo.title)[0]],
        reportDescription: queryInfo.description ? queryInfo.description[Object.keys(queryInfo.description)[0]] : "",
      });
    }
    else {
      this.runQuery(queryInfo);
    }
  };

  runParameterisedQuery = async (queryIndex, params) => {
    const queryInfo = this.state.savedQueries[queryIndex];
    this.runQuery(queryInfo, false, params);
  };

  runQuery = async (queryInfo, predefined = false, params) => {
    const report = uuidv4();
    const playQuery = predefined ? undefined : this.state.playQuery;
    this.setState({
      processing: report,
      displayProcessing: false,
      processingCancelled: false,
      playQuery
    }, async () => {
      try {
        this.loadingTimeout = setTimeout(() => {
          this.setState({ displayProcessing: true });
        }, 1000);
        const queryProjectGraphs = queryInfo.queryProject ? queryInfo.queryProject.map(qp => getGraphFromProject(qp)) : [];
        const resultsData = params ?
          await this.doRunSavedSparql(this.props.graph, queryInfo.id, params).promise
          :
          await this.doRunSparql(this.props.graph, queryInfo.query, queryProjectGraphs).promise;
        if (this.cancellable2 && this.cancellable2.isCancelled()) return;
        if (this.state.cancelledReports.includes(report)) return;
        this.setState({
          processing: "",
          resultsData,
          reportTitle: queryInfo.title[Object.keys(queryInfo.title)[0]],
          reportDescription: queryInfo.description ? queryInfo.description[Object.keys(queryInfo.description)[0]] : "",
          viz: queryInfo.barChart,
          sparqlText: queryInfo.query,
          sparql: false
        });
      }
      catch (e) {
        if (this.cancellable2 && this.cancellable2.isCancelled()) return;
        if (this.state.cancelledReports.includes(report)) return;
        let errorMessage = ["Could not run report."];
        if (e.message === "User is not authorised for querying this project.") {
          errorMessage.push("You do not have permissons for all of the projects used by this report.");
        }
        this.setState({ isErrored: e, errorMessage, processing: "" });
      }
    });
  };

  downloadReport = (format, includeHeaders) => this.processTSV(this.state.resultsData, false, format, includeHeaders);

  processTSV = (data, copy = false, format, includeHeaders) => {

    this.worker = new WebWorker(workerCode);

    this.worker.addEventListener('message',
      (tsv) => {
        try {
          GS.handle();
          if (copy && navigator && navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(tsv.data);
            return;
          }
          const filename = this.state.reportTitle.replace(/[^a-zA-Z0-9]/gi, '_').toLowerCase();
          let bookType = format;
          if (format === "xlsx") {
            const ws = XLSX.utils.aoa_to_sheet(tsv.data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sheet");
            XLSX.writeFile(wb, "graphologi_report_" + filename + '.' + format, {
              bookType
            });
          }
          else {
            // We have to do it this way to stop SheetJS adding a BOM at start of file
            // https://github.com/SheetJS/sheetjs/issues/2620
            let noBOM = tsv.data;
            if (format === "csv") {
              const ws = XLSX.utils.aoa_to_sheet(tsv.data);
              noBOM = XLSX.utils.sheet_to_csv(ws);
            }
            else if (format === "html") {
              const customHeader = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Graphologi Report: ${this.state.reportTitle}</title></head><body>`;
              const ws = XLSX.utils.aoa_to_sheet(tsv.data);
              noBOM = XLSX.utils.sheet_to_html(ws, {
                header: customHeader,
                footer: '</body></html>'
              });
            }
            const url = window.URL.createObjectURL(new Blob([noBOM]));
            const link = document.createElement('a');
            link.href = url;
            const reportName = "graphologi_report_" + filename + "." + format;
            link.setAttribute('download', reportName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }
        }
        catch (e) {
          this.setState({
            isErrored: true,
            errorMessage: "An error occurred downloading the TSV file."
          });
        }
      },
      false);

    this.worker.onerror = () => {
      this.setState({
        isErrored: true,
        errorMessage: "An error occurred generating the TSV file."
      });
    };

    this.worker.postMessage({
      data,
      options: { includeHeaders },
      format
    });

  };

  copyReport = () => this.processTSV(this.state.resultsData, true);

  reset = () => this.setState({ resultsData: undefined, playQuery: undefined });

  resetData = () => this.setState({ resultsData: undefined });

  toggleSPARQL = () => this.setState({ sparql: !this.state.sparql });

  toggleDatatypes = () => this.setState({ showDatatypes: !this.state.showDatatypes });

  printReport = () => window.print();

  clearError = async () => this.setState({ isErrored: false });

  cancelProcessing = () => {
    let cancelledReports = [...this.state.cancelledReports];
    cancelledReports.push(this.state.processing);
    this.setState({ processing: "", cancelledReports });
  };

  render() {

    const { classes, graph, data, entitlements, projectType, projectUsers, id, skosXL, existingComponents } = this.props;

    const components = this.props.data.hasComponent ? this.props.data.hasComponent.map(cp => ({ id: cp })) : [];

    const { isErrored, isLoading, savedQueries, resultsData, reportTitle, errorMessage, processing, displayProcessing,
      reportDescription, viz, sparql, sparqlText, showDatatypes, usable, access, playQuery } = this.state;

    const menuOptions = savedQueries && savedQueries.length > 0 ?
      savedQueries.map((queryInfo, index) => ({
        label: <span data-test={"query-option-" + queryInfo.title[data.defaultLang]} style={{ width: '100%' }}>{queryInfo.title[data.defaultLang]}</span>,
        value: index + 1,
        disabled: queryInfo.queryProject ?
          queryInfo.queryProject.some(qp => !access.includes(qp))
          :
          false
      }))
      :
      [];

    const resultsExist = resultsData && resultsData.results && resultsData.results.bindings.length > 0;
    // We are checking project level entitlements here
    const isEntitled = entitlements && entitlements.includes(APPENTITLEMENTS.CAN_QUERY_PROJECT);
    const canRunSavedQueries = entitlements && entitlements.includes(APPENTITLEMENTS.CAN_RUN_SAVED_QUERY);

    if (!usable) return <div data-test="no-access" style={{ paddingTop: 60, paddingRight: 30, textAlign: 'center' }}>
      <Typography align="center" variant="body2">Reporting is not available with your current subscription.</Typography>
      <Typography align="center" variant="body2">This feature requires a minimum of an Enterprise subscription.</Typography>
      <Upgrade />
    </div>;

    return (
      <div style={{ paddingBottom: 30, minWidth: 800 }}>
        <div className={classes.reports}>
          <div data-test="search-filtering" className={classes.filterArea}>
            <Grid container spacing={2} classes={
              {
                "spacing-xs-2": this.props.classes.fullGrid
              }
            }>
              <Grid item xs={7} sm={6}>
                <Typography className={classes.mainHeading} style={{ paddingTop: 0 }}>Reports</Typography>
              </Grid>
              {isEntitled &&
                <React.Fragment>
                  {resultsData || playQuery !== undefined ?
                    <Grid item xs={6} style={{ textAlign: 'right' }}>
                      <SecondaryButton dataTest="reset-reports" disabled={isLoading || processing !== ""} onClick={this.reset}>Dashboard</SecondaryButton>
                    </Grid>
                    :
                    <React.Fragment>
                      <Grid item xs={3}></Grid>
                      <Grid item xs={3}>
                        {menuOptions.length > 0 &&
                          <MenuSelector
                            disabled={isLoading || processing !== ""}
                            dataTest="report-selector"
                            handleChange={this.selectQuery}
                            hintWord="custom report"
                            options={menuOptions} />
                        }
                      </Grid>
                    </React.Fragment>
                  }
                </React.Fragment>
              }
            </Grid>
          </div>
          {isEntitled ?
            <div style={{ marginBottom: 12 }}>
              <div className={classes.reportResults}>
                {(resultsData || playQuery !== undefined) &&
                  <div style={{ display: 'flex', marginTop: 24, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <Typography data-test="report-title" className={classes.subMainHeading}>{reportTitle}</Typography>
                      {reportDescription && reportDescription !== "" ?
                        reportDescription.split("\n").map((reportLine, index) =>
                          <Typography key={"line-" + index} data-test={"report-description-line-" + index} variant="body2">{reportLine}</Typography>
                        )
                        :
                        null}
                    </div>
                  </div>
                }
                {playQuery !== undefined &&
                  <div>
                    <ReportParameters
                      project={id}
                      disabled={isLoading || processing !== ""}
                      id={this.state.savedQueries[playQuery].id}
                      graph={graph}
                      playQuery={playQuery}
                      entitlements={entitlements}
                      queryRunning={processing !== ""}
                      existingComponents={existingComponents}
                      defaultLang={data.defaultLang}
                      onClear={this.resetData}
                      runQuery={this.runParameterisedQuery}
                      projectType={projectType}
                      projectUsers={projectUsers}
                    />
                  </div>
                }
                {
                  resultsData ?
                    <div>
                      <div id="report-controls" style={{ display: 'flex', width: '100%', textAlign: 'right', marginBottom: 12 }}>
                        <div className={classes.reportSettings} style={{ flex: 1 }}>
                          <Tooltip title="Show/hide the SPARQL used for the report">
                            <IconButton data-test="show-sparql" size="small" onClick={this.toggleSPARQL}>
                              <CodeIcon />
                            </IconButton>
                          </Tooltip>
                          <span style={{ marginLeft: 12 }}>
                            <Tooltip title={showDatatypes ? "Hide datatypes" : "Show datatypes"}>
                              <span>
                                <IconButton
                                  aria-label={showDatatypes ? "Hide datatypes" : "Show datatypes"}
                                  disabled={!resultsExist}
                                  data-test="show-hide-datatypes"
                                  size="small"
                                  onClick={this.toggleDatatypes}>
                                  {showDatatypes ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </span>
                          <span style={{ marginLeft: 12 }}>
                            <Tooltip title="Copy report to the clipboard">
                              <span>
                                <IconButton
                                  disabled={!resultsExist}
                                  aria-label="Copy report to the clipboard"
                                  data-test="copy-report"
                                  size="small"
                                  onClick={this.copyReport}>
                                  <CopyToCipboard />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </span>
                          <span style={{ marginLeft: 12 }}>
                            <Tooltip title="Print the report">
                              <span>
                                <IconButton
                                  aria-label="Print the report"
                                  disabled={!resultsExist}
                                  data-test="print-report"
                                  size="small"
                                  onClick={this.printReport}>
                                  <PrintIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </span>
                        </div>
                        <div style={{ marginRight: 12 }}>
                          <ReportDownload disabled={!resultsExist} onDownloadReport={this.downloadReport} />
                        </div>
                      </div>
                      {sparql &&
                        <div style={{ border: 'solid 1px #e0e0e0', padding: 12, borderRadius: 4, marginBottom: 6 }}>
                          <Typography className={classes.subHeading}>SPARQL Used for Report</Typography>
                          <pre data-test="sparql-text" className={classes.sparql}>
                            {sparqlText}
                          </pre>
                        </div>
                      }
                      <div style={{ backgroundColor: '#f8f8f8', padding: 12 }}>
                        <ReportResultsViewer
                          resultsData={resultsData}
                          project={id}
                          projectType={projectType}
                          projectUsers={projectUsers}
                          graph={graph}
                          showDetail={showDatatypes}
                          components={components}
                          viz={viz}
                          defaultLang={data.defaultLang} />
                      </div>
                    </div>
                    :
                    playQuery !== undefined ?
                      canRunSavedQueries ?
                        <ActionText offset="100px">Set parameters to run report.</ActionText>
                        :
                        <ActionText offset="100px">Report not available.</ActionText>
                      :
                      <ReportsDisplay
                        skosXL={skosXL}
                        runQuery={this.runQuery}
                        projectType={projectType} />
                }
              </div>
            </div>
            :
            <div style={{ height: 200 }}>
              <EntitlementWarning
                requiredEntitlements={[
                  APPENTITLEMENTS.CAN_QUERY_PROJECT
                ]}
              />
            </div>
          }
        </div>
        {isErrored && <Errored data={{ message: errorMessage }} clearError={this.clearError} />}
        {processing !== "" && displayProcessing && <Processing onCancel={this.cancelProcessing} />}
      </div>
    );
  }
}

ReportManager.propTypes = {
  classes: PropTypes.object,
  id: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  skosXL: PropTypes.bool,
  components: PropTypes.array,
  existingComponents: PropTypes.array,
  entitlements: PropTypes.array.isRequired,
  projectType: PropTypes.string.isRequired,
  projectUsers: PropTypes.array,
  graph: PropTypes.string.isRequired
};

export default withStyles(styles)(ReportManager);